import { Box, Button, Flex, IconButton, Text, VStack } from "@chakra-ui/react";
import { useState } from "react";
import { FiChevronDown, FiChevronUp, FiCopy, FiExternalLink } from "react-icons/fi";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useLocation } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";
import {
  buildConsolidatedDataFileName,
  buildPreviewUrlWithPresignedDocumentUrl,
  getBundleExplorerUrl,
  getLocalhostPreviewUrl,
} from "@metriport/shared-internal";
import { useAppContext } from "../contexts/app";
import { isDevEnv, isProdEnv, isStagingEnv } from "../../shared/util";
import useMetriportToast from "../medical/shared-logic/useMetriportToast";
import { useMetriportApi } from "../medical/shared-logic/useMetriportApi";
import { capture } from "../../shared/capture";

/**
 * Gets the AWS region based on the current environment
 * - Production: us-west-1
 * - Staging: us-east-2
 * - Development/Local: us-east-2 (staging)
 * - Sandbox: us-west-2
 */
function getAwsRegion(): string {
  if (isProdEnv()) {
    return "us-west-1";
  }
  if (isStagingEnv() || isDevEnv()) {
    return "us-east-2"; // staging (including local development)
  }
  return "us-west-2"; // sandbox
}

/**
 * Gets the S3 bucket name based on the current environment
 * - Production: metriport-medical-documents
 * - Staging: medical-documents-staging
 * - Development/Local: medical-documents-staging (staging)
 * - Sandbox: metriport-medical-documents-sandbox
 */
function getS3BucketName(): string {
  if (isProdEnv()) {
    return "metriport-medical-documents";
  }
  if (isStagingEnv() || isDevEnv()) {
    return "medical-documents-staging"; // staging (including local development)
  }
  return "metriport-medical-documents-sandbox"; // sandbox
}

/**
 * Builds the S3 console URL for the consolidated data file
 */
function buildS3ConsoleUrl(cxId: string, patientId: string): string {
  const region = getAwsRegion();
  const bucket = getS3BucketName();
  const fileName = `${cxId}_${patientId}_CONSOLIDATED_DATA.json`;
  const prefix = `${cxId}/${patientId}/${fileName}`;

  return `https://${region}.console.aws.amazon.com/s3/object/${bucket}?region=${region}&prefix=${prefix}`;
}

/**
 * Extracts patient ID from the pathname
 * Handles routes like /patient/:patientId and /patient/v2/:patientId
 * Also handles optional sandbox prefix: /sandbox/patient/...
 */
function extractPatientIdFromPath(pathname: string): string | undefined {
  // Match patterns: /patient/:id or /patient/v2/:id (with optional /sandbox prefix)
  const match = pathname.match(/\/patient(?:\/v2)?\/([a-f0-9-]+)/i);
  return match?.[1];
}

/**
 * Floating impersonation toolbar that appears in the upper right corner
 * when impersonating a customer. Shows different content based on the current page.
 */
export function ImpersonationToolbar() {
  const { state } = useAppContext();
  const location = useLocation();
  const toast = useMetriportToast();
  const metriportApi = useMetriportApi();
  const [isMinimized, setIsMinimized] = useLocalStorage("impersonation-toolbar-minimized", false);
  const [loadingPreview, setLoadingPreview] = useState<"localhost" | "explorer" | null>(null);

  // Check if we're on a patient page and extract the patient ID from the path
  const isPatientPage = location.pathname.includes("/patient/");
  const patientId = extractPatientIdFromPath(location.pathname);
  const cxId = state.customer?.id;

  // Show patient-specific tools only on patient page with valid IDs
  const showPatientTools = isPatientPage && patientId && cxId;

  function handleCopySuccess() {
    toast.success({ title: "Customer ID copied to clipboard" });
  }

  function toggleMinimized() {
    setIsMinimized(!isMinimized);
  }

  async function openPreview(type: "localhost" | "explorer") {
    if (!cxId || !patientId) return;
    setLoadingPreview(type);
    try {
      const fileName = buildConsolidatedDataFileName(cxId, patientId);
      const { url } = await metriportApi.getDocumentUrl(fileName);
      const previewBaseUrl =
        type === "localhost" ? getLocalhostPreviewUrl() : getBundleExplorerUrl();
      const previewUrl = buildPreviewUrlWithPresignedDocumentUrl(previewBaseUrl, url);
      window.open(previewUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      capture.error(error, { extra: { context: "impersonation-toolbar.openPreview", type } });
      toast.error({ title: "Failed to get presigned URL" });
    } finally {
      setLoadingPreview(null);
    }
  }

  // Minimized view - just a small indicator
  if (isMinimized) {
    return (
      <IconButton
        position="fixed"
        top="6"
        right="6"
        zIndex="1000"
        size="sm"
        colorScheme="red"
        aria-label="Show impersonation tools"
        icon={<FiChevronDown />}
        onClick={toggleMinimized}
        opacity={0.5}
        _hover={{ opacity: 1 }}
      />
    );
  }

  return (
    <Box
      position="fixed"
      top="6"
      right="6"
      zIndex="1000"
      bg="red.50"
      border="2px"
      borderColor="red.400"
      borderRadius="lg"
      p="4"
      boxShadow="xl"
      minW="200px"
      maxW="300px"
      _dark={{
        bg: "red.900",
        borderColor: "red.300",
      }}
    >
      <VStack align="stretch" gap="3">
        <Flex align="center" justify="space-between">
          <Text fontSize="sm" fontWeight="bold" color="red.700" _dark={{ color: "red.200" }}>
            Impersonation Tools
          </Text>
          <IconButton
            size="xs"
            variant="ghost"
            colorScheme="red"
            aria-label="Minimize toolbar"
            icon={<FiChevronUp />}
            onClick={toggleMinimized}
            _hover={{ bg: "red.100", _dark: { bg: "red.800" } }}
          />
        </Flex>

        {cxId && (
          <Flex
            align="center"
            gap="2"
            p="2"
            bg="red.100"
            borderRadius="md"
            _dark={{ bg: "red.800" }}
          >
            <Text
              fontSize="xs"
              fontWeight="medium"
              color="red.900"
              flex="1"
              isTruncated
              _dark={{ color: "red.100" }}
            >
              {cxId}
            </Text>
            <CopyToClipboard text={cxId} onCopy={handleCopySuccess}>
              <IconButton
                size="xs"
                variant="ghost"
                colorScheme="red"
                aria-label="Copy customer ID"
                icon={<FiCopy />}
              />
            </CopyToClipboard>
          </Flex>
        )}

        {showPatientTools && (
          <>
            <Button
              size="sm"
              colorScheme="red"
              variant="solid"
              rightIcon={<FiExternalLink />}
              onClick={() => {
                const url = buildS3ConsoleUrl(cxId, patientId);
                window.open(url, "_blank", "noopener,noreferrer");
              }}
              w="full"
            >
              View Consolidated Data
            </Button>
            <Button
              size="sm"
              colorScheme="red"
              variant="outline"
              rightIcon={<FiExternalLink />}
              onClick={() => openPreview("localhost")}
              isLoading={loadingPreview === "localhost"}
              isDisabled={loadingPreview !== null}
              w="full"
            >
              Open in Localhost Preview
            </Button>
            <Button
              size="sm"
              colorScheme="red"
              variant="outline"
              rightIcon={<FiExternalLink />}
              onClick={() => openPreview("explorer")}
              isLoading={loadingPreview === "explorer"}
              isDisabled={loadingPreview !== null}
              w="full"
            >
              Open in Bundle Explorer
            </Button>
          </>
        )}

        {!showPatientTools && (
          <Text fontSize="xs" color="red.600" _dark={{ color: "red.300" }}>
            Patient tools available on patient pages
          </Text>
        )}
      </VStack>
    </Box>
  );
}
